import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  ComparisonSelect,
  ComparisonSelectClassNames,
  isNotNullOrUndefined,
} from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../store';
import { MultiSelectFilterData } from '../../store/states/filterTypes';
import {
  getValidComparisonPairFromUrl,
  isValidComparisonSelection,
} from '../../utils/comparisonDetailPageEntities';
import { COMPARISON_ENTITIES_URL_PARAM } from '../../utils/constants';

import { FilterFormatter, mapFilterOptionToSelectData } from './formatter';
import { FILTER_DYNAMIC_WIDTH_SETTINGS } from './util';

function pickOtherComparisonOption(
  options: Array<{ value: string }>,
  exclude: string
): string | undefined {
  return options.find((option) => option.value !== exclude)?.value;
}

export function resolveComparisonSide(
  options: Array<{ value: string }>,
  primary: string,
  secondary: string | null
): string | null {
  if (secondary != null && secondary !== primary) {
    return secondary;
  }
  return pickOtherComparisonOption(options, primary) ?? null;
}

export type ComparisonPageFilterProps = {
  classNames?: ComparisonSelectClassNames;
  entityId: string;
  filterData?: MultiSelectFilterData;
  filterKey: string;
  formatter: FilterFormatter;
  isFetching: boolean;
};

export function ComparisonPageFilter({
  classNames,
  entityId,
  filterData,
  filterKey,
  formatter,
  isFetching,
}: ComparisonPageFilterProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [currentSelection, setFilterSelectionValue] = useStore(
    useShallow((state) => [
      state.filterSelections[entityId]?.[filterKey],
      state.setFilterSelectionValue,
    ])
  );

  const data = useMemo(
    () =>
      filterData?.data
        ?.map((option) => mapFilterOptionToSelectData(option, formatter))
        .filter(isNotNullOrUndefined)
        .map((item) => ({ label: item.label, value: String(item.value) })) ??
      [],
    [filterData?.data, formatter]
  );

  const urlPair = useMemo(() => {
    if (!searchParams.has(COMPARISON_ENTITIES_URL_PARAM)) {
      return undefined;
    }
    return getValidComparisonPairFromUrl(searchParams, filterKey);
  }, [searchParams, filterKey]);

  const selectionArray = Array.isArray(currentSelection)
    ? (currentSelection as string[])
    : [];

  const storePairValid = isValidComparisonSelection(
    searchParams,
    selectionArray
  );

  const baseEntityId = storePairValid
    ? selectionArray[0]
    : (urlPair?.[0] ?? selectionArray[0] ?? null);
  const comparisonEntityId = storePairValid
    ? selectionArray[1]
    : (urlPair?.[1] ?? selectionArray[1] ?? null);

  const selectedOptionsLoaded =
    baseEntityId != null &&
    comparisonEntityId != null &&
    data.some((option) => option.value === baseEntityId) &&
    data.some((option) => option.value === comparisonEntityId);

  const resolvedBaseEntityId = baseEntityId ?? data[0]?.value ?? null;
  const resolvedComparisonEntityId =
    comparisonEntityId ?? data[1]?.value ?? null;

  const baseData = useMemo(
    () => data.filter((d) => d.value !== resolvedComparisonEntityId),
    [data, resolvedComparisonEntityId]
  );
  const comparisonData = useMemo(
    () => data.filter((d) => d.value !== resolvedBaseEntityId),
    [data, resolvedBaseEntityId]
  );

  return (
    <ComparisonSelect
      baseLabel={t('app.pageFilter.comparisonFilter.baseEntity')}
      baseValue={resolvedBaseEntityId}
      baseData={baseData}
      classNames={classNames}
      comparisonLabel={t('app.pageFilter.comparisonFilter.comparisonEntity')}
      comparisonValue={resolvedComparisonEntityId}
      comparisonData={comparisonData}
      data={data}
      disabled={!isFetching && data.length === 0}
      dynamicWidthSettings={FILTER_DYNAMIC_WIDTH_SETTINGS}
      loading={
        isFetching ||
        (selectionArray.length === 2 &&
          !selectedOptionsLoaded &&
          data.length > 0)
      }
      onBaseChange={(value) => {
        if (filterData == null || value == null) {
          return;
        }
        const comparison = resolveComparisonSide(
          data,
          value,
          resolvedComparisonEntityId
        );
        if (comparison == null) {
          return;
        }
        setFilterSelectionValue(entityId, filterKey, [value, comparison]);
      }}
      onComparisonChange={(value) => {
        if (filterData == null || value == null) {
          return;
        }
        const base = resolveComparisonSide(data, value, resolvedBaseEntityId);
        if (base == null) {
          return;
        }
        setFilterSelectionValue(entityId, filterKey, [base, value]);
      }}
      placeholder={t('app.pageFilter.placeholder') ?? undefined}
    />
  );
}
